import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Button } from '../../element/Button/Button';
import { AttrsHelper } from '../../../sb-helpers/attrs-helper';

export type BriefProps = {
  className?: string;
  children: React.ReactNode;
  label?: string;
  briefMaxLines?: number;
  briefMaxChildren?: number | null;
  briefMaxHeight?: string;
  expanded?: boolean | null;
  name?: string;
  hideMask?: boolean;
  pattern?: 'primary' | 'secondary';
  position?: 'left' | 'center' | 'right';
  onToggle?: (opened: boolean) => void;
  /**
   * `minimal` for a brief inside something already small - a message, a cell, a card. Its control
   * drops the button weight and size, so "Show More" reads as a link under the content rather than
   * as the loudest thing in the box.
   */
  variation?: 'default' | 'minimal';
};

/* 16px is CSS's own initial value, and jsdom reports no font size at all. */
const fontSizeOf = (element: Element): number => {
  const size = Number.parseFloat(window.getComputedStyle(element).fontSize);

  return Number.isNaN(size) ? 16 : size;
};

const lineHeightOf = (element: HTMLElement): number => {
  const measured = Number.parseFloat(
    window.getComputedStyle(element).lineHeight
  );

  if (!Number.isNaN(measured)) return measured;

  /*
    `normal` never resolves to a length, and the ratio behind it is the font's own - so one line is
    laid out here and read back. jsdom measures nothing, which is what the ratio below is for.
  */
  const ruler = document.createElement('span');

  ruler.textContent = 'X';
  ruler.style.cssText =
    'position:absolute;visibility:hidden;white-space:nowrap';
  element.append(ruler);

  const { height } = ruler.getBoundingClientRect();

  ruler.remove();

  return height || fontSizeOf(element) * 1.2;
};

/*
  What each CSS length a cap can be written in is relative to. `rem` sits ahead of `em` because
  `'8rem'.endsWith('em')` is also true.
*/
const CAP_UNITS: Array<{
  perUnit: (element: HTMLElement) => number;
  suffix: string;
}> = [
  { perUnit: () => 1, suffix: 'px' },
  { perUnit: () => fontSizeOf(document.documentElement), suffix: 'rem' },
  { perUnit: element => fontSizeOf(element), suffix: 'em' },
  { perUnit: () => window.innerHeight / 100, suffix: 'vh' },
  { perUnit: () => window.innerWidth / 100, suffix: 'vw' },
  {
    perUnit: element => {
      const container = element.parentElement?.clientHeight ?? 0;

      /*
        A percentage against a containing block with no height of its own is `none` in CSS, not
        zero - so there is nothing to resolve and the box comparison answers instead.
      */
      return container > 0 ? container / 100 : Number.NaN;
    },
    suffix: '%',
  },
];

/**
 * **The cap as a number, whatever it was written in.** The content has to be compared with the cap
 * rather than with the box, because the box only wears the cap while the brief is shut - so a
 * brief opened from the start has nothing to read it off and the box comparison can only ever say
 * the content fits, control and all.
 *
 * `null` for a length this cannot resolve, which leaves the box comparison to answer as before.
 */
const capInPixels = (cap: string, element: HTMLElement): number | null => {
  const trimmed = cap.trim();
  const amount = Number.parseFloat(trimmed);

  if (Number.isNaN(amount)) return null;

  // A bare number is only legal CSS at zero, and zero is the same length in every unit.
  if (String(amount) === trimmed) return amount;

  const unit = CAP_UNITS.find(({ suffix }) => trimmed.endsWith(suffix));

  if (!unit) return null;

  const pixels = amount * unit.perUnit(element);

  return Number.isFinite(pixels) ? pixels : null;
};

export const Brief = ({
  className,
  children,
  label,
  briefMaxLines,
  briefMaxChildren,
  briefMaxHeight,
  expanded,
  name,
  hideMask = false,
  pattern = 'primary',
  position = 'center',
  onToggle,
  variation = 'default',
}: BriefProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  // toArray filters the non-rendering artifacts left over from conditional rendering,
  // i.e. showDiv && <div />, so its length is the count a reader would agree with.
  const childArray = React.Children.toArray(children);

  /*
    **A line count becomes a height, rather than a clamp.** `-webkit-line-clamp` only works inside
    a `-webkit-box`, which re-lays-out every block child it holds - so rich text cannot survive it.
    Measured before paint, and only the once: the line height moves with the font, not the content.
  */
  const [lineHeight, setLineHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (briefMaxLines && containerRef.current) {
      setLineHeight(lineHeightOf(containerRef.current));
    }
  }, [briefMaxLines]);

  const heightCap =
    briefMaxHeight ??
    (briefMaxLines && lineHeight
      ? `${briefMaxLines * lineHeight}px`
      : undefined);

  const doesContentExceedMax = () => {
    if (!innerRef.current || !containerRef.current) {
      return true;
    }

    /*
      Measured against the cap rather than against the box, because the cap is only on the box while
      this is closed - open, comparing content to container can only ever say that it fits.
    */
    if (heightCap) {
      const cap = capInPixels(heightCap, containerRef.current);

      if (cap !== null) return innerRef.current.scrollHeight > cap;
    }

    return (
      innerRef.current.clientHeight > containerRef.current.clientHeight ||
      containerRef.current.scrollHeight > containerRef.current.clientHeight ||
      (!!briefMaxChildren && childArray.length > briefMaxChildren)
    );
  };

  // 'hasBeenUsed' fixes bug CR-3663
  const [expandState, setExpandState] = useState<{
    isExpanded: boolean | null;
    contentIsChopped: boolean | null;
    hasBeenUsed: boolean | null;
  }>({
    isExpanded: null,
    contentIsChopped: null,
    hasBeenUsed: null,
  });

  const { contentIsChopped, isExpanded, hasBeenUsed } = expandState;
  /*
    Counted by whether the prop was given, not by what it parses to. `parseInt` reads `0.5rem`,
    `auto` and `0` as falsy, so a caller passing one of those alongside another cap slipped the
    guard and got both applied - the state the error below says is impossible.
  */
  const capsPassed = [briefMaxLines, briefMaxHeight, briefMaxChildren].filter(
    cap => cap !== undefined
  );

  if (capsPassed.length > 1) {
    throw new Error(
      'You can only pass in one prop: briefMaxLines, briefMaxHeight or briefMaxChildren'
    );
  }

  /*
    **Measured again, not once, and after every commit rather than on a dependency read from a ref.**
    A ref's value is read while rendering, so it still describes the last commit - keying on one
    measured the content as it was an item ago, and a list that grew stayed open one item too long.
    The update returns `prev` untouched when nothing moved, which is what settles this.
  */
  useEffect(() => {
    const doesExceed = doesContentExceedMax();

    setExpandState(prev => {
      const next = {
        /*
          Derived until the reader touches the control, then theirs - except that a choice to keep
          it shut only means something while something is hidden. Content that shrank back under
          the cap has nothing left to hide, and holding this false there left the fade and the cap
          behind after the control itself had gone.
        */
        isExpanded: prev.hasBeenUsed
          ? !doesExceed || prev.isExpanded
          : !!(expanded === true || !doesExceed),
        /*
          Sticky while they hold it open: a cap that is only on the box while shut has nothing to
          measure against once open, and reporting that it fits would take "Show Less" with it.
        */
        contentIsChopped:
          prev.hasBeenUsed && prev.isExpanded
            ? prev.contentIsChopped
            : doesExceed,
        hasBeenUsed: prev.hasBeenUsed ?? false,
      };

      return next.isExpanded === prev.isExpanded &&
        next.contentIsChopped === prev.contentIsChopped &&
        next.hasBeenUsed === prev.hasBeenUsed
        ? prev
        : next;
    });
  });

  useEffect(() => {
    setExpandState(prev => ({
      contentIsChopped: doesContentExceedMax(),
      hasBeenUsed: prev.hasBeenUsed,
      isExpanded: expanded === true || prev.isExpanded,
    }));
  }, [expanded]);

  useEffect(() => {
    if (
      hasBeenUsed &&
      !isExpanded &&
      containerRef.current?.scrollIntoView &&
      contentIsChopped !== null
    ) {
      containerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [isExpanded]);

  const processedChildren =
    briefMaxChildren && isExpanded !== true
      ? childArray.slice(0, briefMaxChildren)
      : childArray;

  return (
    <div
      data-test={name}
      className={AttrsHelper.formatClassname(
        'brief',
        variation === 'minimal' && 'brief-minimal',
        className
      )}
    >
      <div
        ref={containerRef}
        className={AttrsHelper.formatClassname(
          'outer-container',
          'brief-content',
          isExpanded && `expanded`,
          hideMask && 'hide-mask'
        )}
        style={
          heightCap && isExpanded !== true
            ? { maxHeight: heightCap, overflow: 'hidden' }
            : undefined
        }
      >
        <div className="inner-container" ref={innerRef}>
          {processedChildren}
        </div>
      </div>
      {!!contentIsChopped && (
        <div className="brief-control">
          <div className={`brief-control-button position-${position}`}>
            <Button
              name="expand-button"
              pattern={pattern}
              variation="minimal"
              icon={isExpanded ? 'arrow-up' : 'arrow-down'}
              onClick={() => {
                onToggle?.(!isExpanded);
                setExpandState(prev => ({
                  contentIsChopped: prev.contentIsChopped,
                  hasBeenUsed: true,
                  isExpanded: !isExpanded,
                }));
              }}
            >
              Show {isExpanded ? 'Less' : 'More'}
              {label ? ` ${label}` : null}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
